import React, { useState, useEffect, useCallback } from 'react';
import { WizardStepProps, PresetLeadFilter, LeadList, LeadData } from '../../../types';
import { getPresetFilters, getUserLeadLists } from '../../../utils/campaignApi';
import { PresetFilterForm } from '../../leads/PresetFilterForm';
import { CSVUploadForm } from '../../leads/CSVUploadForm';
import { PreviousListSelector } from '../../leads/PreviousListSelector';
import { supabase } from '../../../supabase/client';

const LeadManagementStep: React.FC<WizardStepProps> = ({
  state,
  updateState,
  errors
}) => {
  const [presetFilters, setPresetFilters] = useState<PresetLeadFilter[]>([]);
  const [existingLists, setExistingLists] = useState<LeadList[]>([]);
  const [csvPreview, setCsvPreview] = useState<LeadData[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Load preset filters on component mount
  useEffect(() => {
    if (state.lead_acquisition_method === 'preset_filters') {
      loadPresetFilters();
    }
  }, [state.lead_acquisition_method]);

  // Load existing lists on component mount
  useEffect(() => {
    if (state.lead_acquisition_method === 'past_list') {
      loadExistingLists();
    }
  }, [state.lead_acquisition_method]);

  const loadPresetFilters = async () => {
    setIsLoadingFilters(true);
    try {
      const result = await getPresetFilters({ includeInactive: false, limit: 50 });
      if (result.success && result.data) {
        setPresetFilters(result.data);
      } else {
        console.error('Failed to load preset filters:', result.error);
        // Keep empty array as fallback
        setPresetFilters([]);
      }
    } catch (error) {
      console.error('Network error loading preset filters:', error);
      setPresetFilters([]);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const loadExistingLists = async () => {
    setIsLoadingLists(true);
    try {
      const result = await getUserLeadLists({ includeInactive: false, limit: 50 });
      if (result.success && result.data) {
        setExistingLists(result.data);
      } else {
        console.error('Failed to load existing lists:', result.error);
        // Keep empty array as fallback
        setExistingLists([]);
      }
    } catch (error) {
      console.error('Network error loading existing lists:', error);
      setExistingLists([]);
    } finally {
      setIsLoadingLists(false);
    }
  };

  const handlePresetFilterToggle = (filterId: string) => {
    const currentFilters = state.selected_preset_filters || [];
    const updatedFilters = currentFilters.includes(filterId)
      ? currentFilters.filter(id => id !== filterId)
      : [...currentFilters, filterId];
    
    updateState({ selected_preset_filters: updatedFilters });
  };

  const handleExistingListToggle = (listId: string) => {
    const currentLists = state.selected_existing_lists || [];
    const updatedLists = currentLists.includes(listId)
      ? currentLists.filter(id => id !== listId)
      : [...currentLists, listId];
    
    updateState({ selected_existing_lists: updatedLists });
  };

  const handleFileUpload = useCallback(async (file: File) => {
    if (!file.type.includes('csv')) {
      updateState({ 
        validation_errors: { 
          csv_file: ['Please upload a CSV file'] 
        } 
      });
      return;
    }

    setUploadProgress(0);
    updateState({ csv_file: file });

    try {
      // Simulate file processing
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const headers = lines[0]?.split(',').map(h => h.trim()) || [];
        
        const preview = lines.slice(1, 6).map((line, index) => {
          const values = line.split(',').map(v => v.trim());
          const leadData: LeadData = { id: `preview-${index}` };
          
          headers.forEach((header, idx) => {
            if (values[idx]) {
              leadData[header.toLowerCase()] = values[idx];
            }
          });
          
          return leadData;
        }).filter(lead => Object.keys(lead).length > 1);

        setCsvPreview(preview);
        setUploadProgress(100);
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('File upload failed:', error);
      updateState({ 
        validation_errors: { 
          csv_file: ['Failed to process CSV file'] 
        } 
      });
    }
  }, [updateState]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files[0]) {
      handleFileUpload(files[0]);
    }
  };

  const getTotalLeadCount = useCallback(() => {
    switch (state.lead_acquisition_method) {
      case 'preset_filters':
        return presetFilters
          .filter(filter => state.selected_preset_filters?.includes(filter.id))
          .reduce((total, filter) => total + filter.estimated_lead_count, 0);
      
      case 'past_list':
        return existingLists
          .filter(list => state.selected_existing_lists?.includes(list.id))
          .reduce((total, list) => total + list.lead_count, 0);
      
      case 'csv_upload':
        return csvPreview.length > 0 ? csvPreview.length * 200 : 0; // Estimate based on preview
      
      case 'lead_generator':
        return 0; // Will be determined in lead generator step
      
      default:
        return 0;
    }
  }, [state.lead_acquisition_method, state.selected_preset_filters, state.selected_existing_lists, presetFilters, existingLists, csvPreview.length]);

  // Validation handled by parent wizard component

  const handleFilterComplete = useCallback((data: any) => {
    console.log('Filter complete callback received:', data);
    // With draft-first pattern, filter request is created immediately
    updateState({ 
      filter_request_id: data.data?.id,
      lead_source_configured: true 
    });
    
    // If the proceed button was clicked (shouldAdvance flag), move to next step
    if (data.data?.shouldAdvance) {
      // EPOCH 63: Messages is now step 4 (was step 5 before Service Tier removal)
      updateState({ current_step: 4 });
    }
  }, [updateState]);

  const renderPresetFilters = () => {
    if (!state.campaign_id) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Campaign needs to be initialized first. Please go back to Step 1.
          </p>
        </div>
      );
    }
    
    return (
      <PresetFilterForm
        campaignId={state.campaign_id}
        onComplete={handleFilterComplete}
      />
    );
  };

  const handleUploadComplete = useCallback((data: any) => {
    // Update state with upload info
    updateState({ 
      csv_upload_id: data.data?.id,
      lead_source_configured: true 
    });
    
    // If the complete button was clicked (shouldAdvance flag), move to next step
    if (data.data?.shouldAdvance) {
      // EPOCH 63: Messages is now step 4 (was step 5 before Service Tier removal)
      updateState({ current_step: 4 });
    }
  }, [updateState]);

  const renderCsvUpload = () => {
    if (!state.campaign_id) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Campaign needs to be initialized first. Please go back to Step 1.
          </p>
        </div>
      );
    }
    
    // Note: CSVUploadForm should handle fetching company_id internally
    return (
      <CSVUploadForm
        campaignId={state.campaign_id}
        companyId={''}  // Let CSVUploadForm handle this
        onComplete={handleUploadComplete}
      />
    );
  };

  const renderCsvUploadOld = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Lead CSV</h3>
        <p className="text-gray-600">Upload a CSV file containing your lead data. Include columns for firstname, lastname, company, position, and linkedin_url.</p>
      </div>

      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
          dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {state.csv_file ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">{state.csv_file.name}</p>
              <p className="text-gray-500">{(state.csv_file.size / 1024).toFixed(1)} KB</p>
            </div>
            {uploadProgress < 100 && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                updateState({ csv_file: undefined });
                setCsvPreview([]);
                setUploadProgress(0);
              }}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Remove file
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-gray-100 rounded-full">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">Drop your CSV file here</p>
              <p className="text-gray-500">or click to browse</p>
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file);
              }}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
            >
              Choose File
            </label>
          </div>
        )}
      </div>

      {csvPreview.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Preview (first 5 rows)</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  {Object.keys(csvPreview[0] || {}).map((key) => (
                    <th key={key} className="text-left py-2 px-3 font-medium text-gray-700 capitalize">
                      {key.replace('_', ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvPreview.map((lead, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    {Object.values(lead).map((value, colIndex) => (
                      <td key={colIndex} className="py-2 px-3 text-gray-900">
                        {String(value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const handlePreviousListComplete = useCallback(async (data: any) => {
    // Update local state
    updateState({ 
      selected_existing_lists: [data.data.list_id],
      lead_source_configured: true,
      // Store the list name in state for display in review step
      selected_list_name: data.data.list_name,
      lead_source_id: data.data.list_id
    });
    
    // Save lead_source_id to database immediately
    if (state.campaign_id && data.data.list_id) {
      try {
        const { error } = await supabase
          .from('campaigns')
          .update({ 
            lead_source_id: data.data.list_id,
            lead_source_type: 'past_list'
          })
          .eq('id', state.campaign_id);
          
        if (error) {
          console.error('Failed to save lead_source_id:', error);
        }
      } catch (err) {
        console.error('Error saving lead_source_id:', err);
      }
    }
    
    // If the proceed button was clicked (shouldAdvance flag), move to next step
    if (data.data?.shouldAdvance) {
      // EPOCH 63: Messages is now step 4 (was step 5 before Service Tier removal)
      updateState({ current_step: 4 });
    }
  }, [updateState, state.campaign_id]);

  const renderExistingLists = () => {
    if (!state.campaign_id) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Campaign needs to be initialized first. Please go back to Step 1.
          </p>
        </div>
      );
    }
    
    return (
      <PreviousListSelector
        campaignId={state.campaign_id}
        onComplete={handlePreviousListComplete}
      />
    );
  };

  const renderLeadGenerator = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Lead Generator</h3>
        <p className="text-gray-600">Use our Lead Generator to create a custom lead list with advanced filtering options.</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">Lead Generator Features</h4>
            <div className="mt-2 text-sm text-blue-700">
              <p>The Lead Generator allows you to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Select leads by Tiers (Tiers indicate if the leads had taken an action before)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <button
          onClick={() => {
            // Open Lead Generator in new tab to preserve campaign creation state
            window.open('/lead-generator', '_blank');
          }}
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Open Lead Generator
        </button>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Opens in a new tab. Once you've created your lead list, return here to continue your campaign setup.
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 14.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-yellow-800">Campaign Setup Note</h4>
            <p className="text-sm text-yellow-700 mt-1">
              After creating your lead list in the Lead Generator, you can either:
              <br />• Return here and select "Existing Lists" to use your new lead list
              <br />• Continue with "Lead Generator" and we'll prompt you to select from your created lists
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Update total estimated leads when selections change
  useEffect(() => {
    const totalLeads = getTotalLeadCount();
    updateState({ total_estimated_leads: totalLeads });
  }, [getTotalLeadCount, updateState]);

  // Gate rendering on branch decision - prevent race condition
  if (state.lead_acquisition_method === null) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse">
          <div className="h-48 bg-gray-200 rounded"></div>
          <p className="text-gray-500 mt-4 text-center">Loading your selection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Method-specific content */}
      <div className="bg-white">
        {state.lead_acquisition_method === 'preset_filters' && renderPresetFilters()}
        {state.lead_acquisition_method === 'csv_upload' && renderCsvUpload()}
        {state.lead_acquisition_method === 'past_list' && renderExistingLists()}
        {state.lead_acquisition_method === 'lead_generator' && renderLeadGenerator()}
      </div>

      {/* Lead count summary */}
      {getTotalLeadCount() > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Estimated Total Leads:</span>
            <span className="text-lg font-bold text-blue-600">
              {getTotalLeadCount().toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Error display */}
      {errors && Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-700">
            {Object.entries(errors).map(([field, fieldErrors]) => (
              <div key={field}>
                {fieldErrors.map((error, index) => (
                  <p key={index}>{error}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default LeadManagementStep;